import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { DualBasisLine, ProfitAndLoss } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { ErrorBanner, Loading, Money, Panel, formatDate, money, useDateRange } from '../../components/ui'

/**
 * The headline report (BUILD_PLAN §3.1, §10.1).
 *
 * Cash basis and accrual basis side by side with a difference column. The document asks for this
 * in steps 4 and 5, and no off-the-shelf package does it well because most assume you picked one
 * basis. The reconciliation underneath proves the two columns are consistent.
 */
export function ProfitAndLossPage() {
  const { can } = useAuth()
  const { from, to, setFrom, setTo } = useDateRange()

  const report = useQuery<ProfitAndLoss>(`/reports/profit-and-loss${query({ from, to })}`, [from, to])

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="spacer" />
        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(`/reports/profit-and-loss/excel${query({ from, to })}`, 'profit-and-loss.xlsx')
            }
          >
            Export to Excel
          </button>
        )}
        {can(P.reports.taxPack) && (
          <button
            className="primary"
            onClick={() =>
              void download(`/reports/tax-advisor-pack${query({ from, to })}`, 'tax-advisor-pack.xlsx')
            }
          >
            Tax advisor pack
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading what="Building both columns" />}

      {report.data && (
        <>
          {/* The reconciliation is surfaced on the report, not buried in a test, so a wrong basis
              flag shows up during the close rather than at year end (BUILD_PLAN §8). */}
          {!report.data.reconciliation.holds && (
            <div className="banner warn">
              <div>
                <strong>The two bases do not reconcile.</strong> {money(report.data.reconciliation.unexplained)}{' '}
                is unexplained, which means a cash movement carries the wrong basis flag. The accrual
                result less the cash result should equal the movement in receivables, prepayments,
                employee custody, payables, accruals and unsettled tax, less non-cash charges.
              </div>
            </div>
          )}

          <Panel
            title={
              <h2>
                Profit &amp; loss{' '}
                <span className="muted" style={{ fontWeight: 400 }}>
                  · {formatDate(report.data.from)} to {formatDate(report.data.to)}
                </span>
              </h2>
            }
            flush
          >
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Code</th>
                    <th>Account</th>
                    <th style={{ width: 140 }} className="num">
                      Cash basis
                    </th>
                    <th style={{ width: 140 }} className="num">
                      Accrual basis
                    </th>
                    <th style={{ width: 140 }} className="num">
                      Difference
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <Section
                    heading="Revenue"
                    lines={report.data.revenue}
                    total={report.data.totalRevenue}
                  />
                  <Section
                    heading="Expenses"
                    lines={report.data.expenses}
                    total={report.data.totalExpenses}
                  />

                  <tr className="subtotal">
                    <td />
                    <td>NET RESULT</td>
                    <td className="num">
                      <Money value={report.data.netResult.cash} />
                    </td>
                    <td className="num">
                      <Money value={report.data.netResult.accrual} />
                    </td>
                    <td className="num">
                      <Money value={report.data.netResult.difference} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Reconciliation — accrual result less cash result">
            <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
              Every movement is stated as an increase in that balance. If the residual is not zero,
              a basis flag is wrong somewhere.
            </p>

            <table>
              <tbody>
                <ReconciliationRow label="Difference in result" value={report.data.reconciliation.resultDifference} />
                <ReconciliationRow label="Increase in receivables" value={report.data.reconciliation.receivableMovement} />
                <ReconciliationRow label="Increase in prepayments" value={report.data.reconciliation.prepaidMovement} />
                <ReconciliationRow label="Increase in employee custody" value={report.data.reconciliation.custodyMovement} />
                <ReconciliationRow label="Increase in payables" value={-report.data.reconciliation.payableMovement} />
                <ReconciliationRow label="Increase in accruals and payroll deductions" value={-report.data.reconciliation.accrualMovement} />
                <ReconciliationRow label="Tax recognized but not yet settled in cash" value={-report.data.reconciliation.taxMovement} />
                <ReconciliationRow label="Non-cash charges (depreciation)" value={-report.data.reconciliation.nonCashCharges} />
                <ReconciliationRow label="Other balance-sheet movements without cash" value={report.data.reconciliation.otherMovement} />
                <tr className="subtotal">
                  <td>Unexplained</td>
                  <td className="num">
                    <Money value={report.data.reconciliation.unexplained} />
                  </td>
                  <td>
                    {report.data.reconciliation.holds ? (
                      <span className="tag ok">Reconciles</span>
                    ) : (
                      <span className="tag danger">Does not reconcile</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </>
  )
}

function Section({
  heading,
  lines,
  total,
}: {
  heading: string
  lines: DualBasisLine[]
  total: DualBasisLine
}) {
  return (
    <>
      <tr>
        <td colSpan={5} className="strong" style={{ paddingTop: 14 }}>
          {heading.toUpperCase()}
        </td>
      </tr>

      {lines.length === 0 && (
        <tr>
          <td />
          <td colSpan={4} className="muted">
            Nothing in this period.
          </td>
        </tr>
      )}

      {lines.map((line) => (
        <tr key={line.code}>
          <td className="code">{line.code}</td>
          <td>
            {line.nameEn}
            <span className="muted"> · {line.nameAr}</span>
          </td>
          <td className="num">
            <Money value={line.cash} blankZero />
          </td>
          <td className="num">
            <Money value={line.accrual} blankZero />
          </td>
          <td className="num">
            <Money value={line.difference} blankZero />
          </td>
        </tr>
      ))}

      <tr className="subtotal">
        <td />
        <td>{total.nameEn}</td>
        <td className="num">
          <Money value={total.cash} />
        </td>
        <td className="num">
          <Money value={total.accrual} />
        </td>
        <td className="num">
          <Money value={total.difference} />
        </td>
      </tr>
    </>
  )
}

function ReconciliationRow({ label, value }: { label: string; value: number }) {
  return (
    <tr>
      <td>{label}</td>
      <td className="num" style={{ width: 160 }}>
        <Money value={value} />
      </td>
      <td />
    </tr>
  )
}
